import React, { Suspense, useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, BookOpen, Search, Sword, Swords, Users, Zap, ScrollText, Globe, Star, Library, Moon, Sun, Store, Construction, ChevronDown, Heart, LogOut, UserCog, LogIn, UserPlus, Flag, Gem, Hammer, ShieldCheck, User, LayoutGrid, Tent, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { isPageUnderConstruction } from '../config/underConstruction';
import { allFamilies, getFamilyByKey } from '../config/familyRegistry';
import { getActiveSystem } from '../config/system';
import { track } from '../utils/analytics';
import SystemSelector from './SystemSelector';
import TypographyMenu from './TypographyMenu';
import AdminMenu from './AdminMenu';
import OmniSearch from './OmniSearch';
import { REPORT_PROBLEM_EVENT } from './reportProblemEvent';
import CommandPalette from './CommandPalette';
import HoverCardLayer from './generic/HoverCardLayer';
import OfflineNotice from './OfflineNotice';
import HandleBanner from './HandleBanner';
import SessionEndedNotice from './SessionEndedNotice';
import { prefetchFromEvent } from '../config/routeChunks';
import { resolveContentHref, shouldRouteClick } from '../utils/contentLinks';
import { INVITES_CHANGED_EVENT } from './invitesEvents';
import { WIKI_UNREAD_EVENT } from './wikiEvents';
import apiClient from '../config/axios';
import { useAuth } from '../context/AuthContext';
import { schedulePush, PREFERENCES_APPLIED_EVENT } from '../config/preferencesSync';
import { getTheme, setTheme, applyTheme } from '../config/theme';
import lazyPage from '../utils/lazyPage';

const INVITE_COUNT_MIN_INTERVAL_MS = 60_000;

// The problem-report modal (#59) loads the first time someone opens it: it is
// on every page and almost never used, and it was 9.6 kB of the main script
// (measured 2026-10-15). A deploy that removed its chunk reloads once, as a
// lazy page does.
const loadReportModal = () => import('./ReportModal');
const ReportModal = lazyPage(loadReportModal);
// Warm it when the pointer or focus reaches the footer button, as
// routeChunks does for a lazy page's links.
const warmReportModal = () => { loadReportModal().catch(() => {}); };

const Layout = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // The full header bar only while it fits (2026-10-05): with a larger text size or the dyslexia-friendly font, a
  // signed-in header ran up to 300 px past a 1280 px window and the page scrolled sideways. When the bar's row
  // overflows, the header takes the compact menu phones use, and keeps it until the window is as wide as the bar
  // needed. Measured again whenever what the bar holds or how it is set can change.
  const headerRowRef = useRef(null);
  const neededRef = useRef(0);
  const [compactHeader, setCompactHeader] = useState(false);
  // The mode as last decided, and when it last changed: a measure decides from these, never inside a state updater, and
  // more than three changes in a second settle on compact — a size or font change mid-flight once flipped the bar back
  // and forth until React stopped it (React error #185, QA retest of #1085).
  const compactRef = useRef(false);
  const flipsRef = useRef([]);
  const decide = useCallback((next) => {
    if (next === compactRef.current) return;
    const now = Date.now();
    flipsRef.current = flipsRef.current.filter((t) => now - t < 1000);
    if (flipsRef.current.length >= 3) next = true;
    if (next === compactRef.current) return;
    flipsRef.current.push(now);
    compactRef.current = next;
    setCompactHeader(next);
  }, []);
  const measureHeader = useCallback(() => {
    const row = headerRowRef.current;
    if (!row || typeof window === 'undefined') return;
    if (window.innerWidth < 768) { decide(false); return; } // below md the phone layout applies anyway
    // Judged against the row, which sits in a centred container no wider than 1280 px: the full bar must fit the row
    // without overflowing it or squeezing the name. `neededRef` is the width it took, read while it was shown.
    // A site name squeezed onto two or three lines is a bar that does not fit either (it may shrink a little, as it
    // does at 1280 with the default text): what it lacks is the width it would take on one line.
    const logo = row.querySelector('.logo-text');
    let squeezed = 0;
    if (logo && logo.getClientRects().length > 1) {
      const probe = logo.cloneNode(true);
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap';
      logo.parentNode.appendChild(probe);
      squeezed = Math.max(0, probe.getBoundingClientRect().width - logo.getBoundingClientRect().width);
      probe.remove();
    }
    if (compactRef.current) { decide(row.clientWidth < neededRef.current + 8); return; }
    if (squeezed > 0 || row.scrollWidth - row.clientWidth > 1) { neededRef.current = row.scrollWidth + squeezed; decide(true); }
  }, [decide]);
  const remeasureHeader = useCallback(() => { neededRef.current = 0; decide(false); }, [decide]);
  useLayoutEffect(() => { measureHeader(); });
  useEffect(() => { remeasureHeader(); }, [isAuthenticated, user?.is_admin, remeasureHeader]);
  useEffect(() => {
    window.addEventListener('resize', measureHeader);
    const watch = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(remeasureHeader) : null;
    if (watch) watch.observe(document.documentElement, { attributes: true, attributeFilter: ['data-textsize', 'data-font', 'data-density'] });
    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (fonts && fonts.addEventListener) fonts.addEventListener('loadingdone', remeasureHeader);
    // The bar's own pieces growing — a web font arriving after the first measure, a menu label changing — is a
    // re-measure too: the fonts' event alone missed a header measured with its fallback font.
    const grows = typeof ResizeObserver !== 'undefined' && headerRowRef.current ? new ResizeObserver(() => measureHeader()) : null;
    if (grows) for (const child of headerRowRef.current.children) grows.observe(child);
    return () => {
      if (grows) grows.disconnect();
      window.removeEventListener('resize', measureHeader);
      if (watch) watch.disconnect();
      if (fonts && fonts.removeEventListener) fonts.removeEventListener('loadingdone', remeasureHeader);
    };
  }, [measureHeader, remeasureHeader]);
  // A breakpoint class as the header's mode reads it: compact, the md: desktop pieces hide and the phone pieces show.
  const bp = (cls) => (compactHeader ? cls.replace(/\bmd:hidden\b/g, '').replace(/\bmd:(flex|block)\b/g, '') : cls);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Back to the full bar on a desktop window: the compact menu and search row it opened close with it.
  useEffect(() => {
    if (!compactHeader && typeof window !== 'undefined' && window.innerWidth >= 768) { setIsMenuOpen(false); setIsSearchOpen(false); }
  }, [compactHeader]);
  const [isReportOpen, setIsReportOpen] = useState(false);
  // Mounted from the first opening on, so its own close handling runs as before.
  const [reportUsed, setReportUsed] = useState(false);
  useEffect(() => { if (isReportOpen) setReportUsed(true); }, [isReportOpen]);
  // A page may open the report filled (issue 633: the roster's "Report @handle").
  const [reportInitial, setReportInitial] = useState(null);
  useEffect(() => {
    const onReport = (e) => { setReportInitial((e && e.detail) || null); setIsReportOpen(true); };
    window.addEventListener(REPORT_PROBLEM_EVENT, onReport);
    return () => window.removeEventListener(REPORT_PROBLEM_EVENT, onReport);
  }, []);
  const [openDropdown, setOpenDropdown] = useState(null);
  // The menu hovering opened: a click on its name then keeps it open instead
  // of toggling it shut (issue 1237).
  const hoverOpened = useRef(null);
  // Opened by ArrowDown: focus moves to the first item once it renders.
  const focusFirstItem = useRef(null);
  const [openMobileGroups, setOpenMobileGroups] = useState({});
  // Pending campaign invites (issue 500 §4). With no email in the stack the
  // count on the account link IS how a person learns an invite arrived, so it
  // is fetched on sign-in, when the window regains focus, and when the games
  // tab decides one.
  //
  // THROTTLED: a focus event fires on every page load and every switch back
  // to the tab, and this is a request against the app limiter (100/min per
  // IP, shared by everything else the page does). The first deployed e2e run
  // of issue 500 fired it on mount and on each focus, twice each as the
  // session hydrated, and the GM's campaign read was answered 429. So: one
  // fetch per sign-in, a refetch on focus at most once a minute, and an
  // immediate one only when the games tab has just decided an invite.
  const [inviteCount, setInviteCount] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) { setInviteCount(0); return undefined; }
    let cancelled = false;
    let last = 0;
    const refresh = (force) => {
      const now = Date.now();
      if (!force && now - last < INVITE_COUNT_MIN_INTERVAL_MS) return;
      last = now;
      apiClient.get('/api/users/me/invites/count')
        .then((res) => { if (!cancelled) setInviteCount(Number(res.data && res.data.pending) || 0); })
        .catch(() => {});
    };
    const onFocus = () => refresh(false);
    const onChanged = () => refresh(true);
    // The session hydration (/me) carries the count, so a boot makes no
    // request of its own here; only a sign-in payload lacks it. A PROVISIONAL
    // session (issue 644: the claims of the stored token, before /me answers)
    // is neither — it waits for /me to replace it, which re-runs this effect.
    if (user && user.provisional) return undefined;
    if (user && typeof user.pending_invites === 'number') {
      setInviteCount(user.pending_invites);
      last = Date.now();
    } else {
      refresh(true);
    }
    window.addEventListener('focus', onFocus);
    window.addEventListener(INVITES_CHANGED_EVENT, onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(INVITES_CHANGED_EVENT, onChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user && user.provisional]);
  // "New in the wiki" (issue 1570): an announced reveal the person has not marked read, in any campaign wiki.
  // It rides the session hydration (`/api/auth/me`'s `wiki_unread`): no request of its own, on boot or after;
  // Newly revealed's Mark all read says what is left (`WIKI_UNREAD_EVENT`).
  const [wikiNew, setWikiNew] = useState(false);
  useEffect(() => {
    setWikiNew(!!(isAuthenticated && user && !user.provisional && user.wiki_unread === true));
  }, [isAuthenticated, user]);
  useEffect(() => {
    const onWiki = (e) => setWikiNew(!!(e && e.detail && e.detail.unread));
    window.addEventListener(WIKI_UNREAD_EVENT, onWiki);
    return () => window.removeEventListener(WIKI_UNREAD_EVENT, onWiki);
  }, []);
  const wikiMarker = wikiNew ? (
    <span className="wiki-new-marker" data-testid="header-wiki-new" aria-label="New in the wiki" title="New in the wiki" />
  ) : null;
  const inviteBadge = inviteCount > 0 ? (
    <span
      className="badge badge-yellow"
      data-testid="invite-count"
      aria-label={`${inviteCount} pending ${inviteCount === 1 ? 'invitation' : 'invitations'}`}
    >
      {inviteCount}
    </span>
  ) : null;
  // Initialize from the class the pre-paint bootstrap (public/index.html) set,
  // so the toggle icon is correct on first render. Defaults to dark.
  const [isDarkMode, setIsDarkMode] = useState(
    () => typeof document === 'undefined' || document.documentElement.classList.contains('dark')
  );
  const location = useLocation();
  const navigate = useNavigate();
  // In-content links (issue 591). Authored prose renders as plain anchors
  // (RichText), so a click on one used to reload the whole app and arrive
  // with no router state — the back link and the breadcrumb lost the list the
  // reader came from. One delegated handler routes a same-origin anchor
  // inside authored prose through the router, rewriting the ingest-era path
  // form to the query form on the way (no redirect hop), and threads the
  // current page as `from` and the list the reader started at as `listFrom`,
  // which is exactly what a detail page reads. A modified click, a new-tab
  // target, a download or another origin is left to the browser.
  const routeContentClick = useCallback((e) => {
    const anchor = e.target && e.target.closest && e.target.closest('.prose-content a[href]');
    if (!anchor || !shouldRouteClick(e, anchor)) return;
    const to = resolveContentHref(anchor.getAttribute('href'));
    if (!to) return;
    e.preventDefault();
    const here = `${location.pathname}${location.search}`;
    const listFrom = (location.state && (location.state.listFrom || location.state.from)) || null;
    navigate(to, { state: { from: here, ...(listFrom ? { listFrom } : {}) } });
  }, [location, navigate]);

  useEffect(() => {
    // Default to dark regardless of the OS color-scheme setting; only an
    // explicit, persisted user choice shows another theme from the set (#314).
    // The initial class + data-theme are set pre-paint by theme-bootstrap.js;
    // this re-applies through the one shared applier and keeps React's toggle
    // state in sync.
    applyTheme(getTheme());
    setIsDarkMode(document.documentElement.classList.contains('dark'));
  }, []);

  // A synced preferences apply (issue 291) rewrites the theme key and class
  // underneath React — resync the toggle's state from the document, the same
  // source the mount effect reads.
  useEffect(() => {
    const onApplied = () =>
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    window.addEventListener(PREFERENCES_APPLIED_EVENT, onApplied);
    return () => window.removeEventListener(PREFERENCES_APPLIED_EVENT, onApplied);
  }, []);

  const toggleDarkMode = () => {
    // The header toggle stays a quick dark/light switch. From one of the
    // named accessibility themes (#314) it lands on plain light — the full
    // set is picked from the typography menu's Theme group.
    const newDarkMode = !isDarkMode;
    track('theme-toggle', { theme: newDarkMode ? 'dark' : 'light' });
    setIsDarkMode(newDarkMode);
    setTheme(newDarkMode ? 'dark' : 'light');
    // Synced preferences (issue 291): push the change to the account
    // (no-op signed out).
    schedulePush();
  };

  // Nav membership is DERIVED from the Family registry (05 §R1). It used to be
  // this hardcoded list — one of the four static copies of the ten entity types
  // that the re-platforming was supposed to delete, and the only one users can
  // see. `allFamilies()` has existed and been unit-tested since P4 and had no
  // consumer at all; this is that consumer.
  //
  // What stays local is PRESENTATION, because the registry does not carry it:
  // which of the two groups a Family belongs to, and its icon. A Family with no
  // entry in these maps still appears — in World Building, with a default icon —
  // so a newly declared Family reaches the nav on its own, which is the whole
  // point and what the user guide promises. Grouping is per-segment rather than
  // ordinal so it cannot silently reshuffle when nav_order changes.
  const NAV_GROUP = {
    spells: 'character', feats: 'character', classes: 'character', races: 'character',
    items: 'character', skills: 'character', languages: 'character',
    monsters: 'world', deities: 'world', rulebooks: 'world',
  };
  const NAV_ICON = {
    spells: Zap, feats: Sword, classes: Users, races: Users, items: ScrollText,
    skills: Globe, languages: Star, monsters: BookOpen, deities: Library,
    rulebooks: BookOpen,
  };

  const familyItems = (groupKey) => allFamilies(getActiveSystem())
    .filter((f) => (NAV_GROUP[f.segment] || 'world') === groupKey)
    .map((f) => ({
      name: f.labels.plural,
      href: `/${f.segment}`,
      icon: NAV_ICON[f.segment] || BookOpen,
    }));

  const navGroups = [
    {
      name: 'Character Building',
      key: 'character',
      items: familyItems('character'),
    },
    {
      name: 'World Building',
      key: 'world',
      items: [
        ...familyItems('world'),
        // Tools are not Families and are not in the registry.
        { name: 'Shop Generator', href: '/shop', icon: Store },
        // Treasure Hoard Generator (#145): budget/CR-fit loot drops.
        { name: 'Treasure Generator', href: '/treasure', icon: Gem },
        // Trap Builder (issue 1584): only where the system prints trap-design rules.
        ...(getFamilyByKey('traprule', getActiveSystem()) ? [{ name: 'Trap Builder', href: '/trap-builder', icon: AlertTriangle }] : []),
        // Usable without an account (#140 follow-up): the tracker + encounter
        // generator run in-memory for guests; only saving/sharing needs login.
        { name: 'Initiative Tracker', href: '/tracker', icon: Swords },
        // DM screen (issue 200): pinned references, one screen. Login-gated
        // inside the page (Favorites pattern), so the link stays visible.
        { name: 'DM Screen', href: '/dm-screen', icon: LayoutGrid },
      ],
    },
  ];

  const isActive      = (href) => location.pathname === href || location.pathname.startsWith(href + '/');
  const isGroupActive = (group) => group.items.some((item) => isActive(item.href));

  useEffect(() => {
    setOpenDropdown(null);
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname]);

  // The two top-bar menus from the keyboard (issue 1237): ArrowDown on a
  // menu's name opens it and steps in; the arrows (and Home/End) move through
  // its links; Escape closes it and puts focus back on its name; focus
  // leaving the menu closes it. The links stay ordinary Tab stops.
  useEffect(() => {
    if (!openDropdown || focusFirstItem.current !== openDropdown) return;
    focusFirstItem.current = null;
    document.querySelector(`[data-nav-menu="${openDropdown}"] .dropdown-item`)?.focus();
  }, [openDropdown]);

  const navMenuKey = (groupKey) => (e) => {
    const wrap = e.currentTarget;
    const button = wrap.querySelector('button');
    const items = Array.from(wrap.querySelectorAll('.dropdown-item'));
    const at = items.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      if (openDropdown !== groupKey) return;
      e.preventDefault();
      setOpenDropdown(null);
      button?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (openDropdown !== groupKey) {
        focusFirstItem.current = groupKey;
        setOpenDropdown(groupKey);
      } else {
        items[(at + 1) % items.length]?.focus();
      }
    } else if (e.key === 'ArrowUp' && at >= 0) {
      e.preventDefault();
      items[(at - 1 + items.length) % items.length]?.focus();
    } else if (e.key === 'Home' && at >= 0) {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End' && at >= 0) {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  };

  const navMenuBlur = (groupKey) => (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setOpenDropdown((prev) => (prev === groupKey ? null : prev));
  };

  return (
    // Hovering, focusing or touching ANY link to a lazily loaded page warms
    // its chunk (config/routeChunks.js), so the round trip code splitting
    // added is paid before the click. One delegated handler, every link.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="site-shell min-h-screen flex flex-col bg-secondary text-default"
      onMouseOver={prefetchFromEvent}
      onFocusCapture={prefetchFromEvent}
      onTouchStart={prefetchFromEvent}
      onClick={routeContentClick}
    >

      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6">
          <div ref={headerRowRef} data-compact={compactHeader ? 'true' : undefined} className="flex justify-between items-center h-14 md:h-[4.5rem]">

            {/* Logo + Desktop Nav */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2.5">
                {/* Emblem */}
                <div
                  className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(201,168,76,0.12)',
                    border: '1.5px solid rgba(201,168,76,0.55)',
                  }}
                >
                  <span style={{ fontFamily: 'Cinzel, Georgia, serif', color: '#C9A84C', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                    DT
                  </span>
                </div>
                {/* Hide the wordmark on very narrow phones — the DT emblem carries the brand */}
                <span className="logo-text hidden min-[400px]:inline">The Dragon's Table</span>
              </Link>

              {/* Desktop dropdown nav */}
              <nav aria-label="Primary" className={bp('hidden md:flex items-center gap-1 ml-4')}>
                {navGroups.map((group) => {
                  const groupActive = isGroupActive(group);
                  return (
                    <div
                      key={group.key}
                      className="dropdown"
                      data-nav-menu={group.key}
                      onMouseEnter={() => {
                        if (openDropdown !== group.key) hoverOpened.current = group.key;
                        setOpenDropdown(group.key);
                      }}
                      onMouseLeave={() => {
                        hoverOpened.current = null;
                        setOpenDropdown((prev) => (prev === group.key ? null : prev));
                      }}
                      onKeyDown={navMenuKey(group.key)}
                      onBlur={navMenuBlur(group.key)}
                    >
                      <button
                        type="button"
                        className={`nav-link ${groupActive ? 'nav-link-active' : 'nav-link-inactive'}`}
                        aria-expanded={openDropdown === group.key}
                        onClick={() => {
                          if (hoverOpened.current === group.key) {
                            // Hover already opened it: the click confirms, never closes.
                            hoverOpened.current = null;
                            setOpenDropdown(group.key);
                            return;
                          }
                          setOpenDropdown((prev) => (prev === group.key ? null : group.key));
                        }}
                      >
                        <span>{group.name}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === group.key ? 'rotate-180' : ''}`} />
                      </button>

                      {openDropdown === group.key && (
                        <div className="dropdown-menu">
                          {group.items.map((item) => {
                            const Icon = item.icon;
                            const isUnderConstruction = isPageUnderConstruction(item.href);
                            const active = isActive(item.href);
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                className={`dropdown-item ${active ? 'dropdown-item-active' : 'dropdown-item-inactive'}`}
                              >
                                <Icon className="w-4 h-4 flex-shrink-0" />
                                <span>{item.name}</span>
                                {isUnderConstruction && (
                                  <Construction className="w-3.5 h-3.5 ml-auto" style={{ color: '#C9A84C' }} />
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>

            {/* Search + auth + system selector + dark mode toggle + mobile burger */}
            <div className="flex items-center gap-1">
              {/* Global omnisearch (#139): inline box on desktop; below md it
                  collapses to an icon that opens a full-width row (see below) */}
              <div className={bp('hidden md:block w-32')}>
                <OmniSearch />
              </div>
              {/* Auth cluster: desktop only — below md it lives in the mobile menu
                  (the 360px top bar can't fit it, issue P0.3) */}
              {/* The icons sit tighter than the text links (issue 1069): with an admin's menu the cluster must still fit 1280 px.
                  Each icon has its word under it (decision #1442): an icon alone was a guess for a newcomer. */}
              <div className={bp('header-icons hidden md:flex items-center gap-1')}>
                {isAuthenticated ? (
                  <>
                    {/* The admin's tools in one menu (issue 1069): three icons pushed an admin's header past 1280 px. */}
                    {user?.is_admin && <AdminMenu isActive={isActive} />}
                    <Link
                      to="/authoring"
                      className={`nav-link ${isActive('/authoring') ? 'nav-link-active' : 'nav-link-inactive'}`}
                      aria-label="My content"
                      title="My game systems and homebrew"
                    >
                      <Hammer className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">My content</span>
                    </Link>
                    <Link
                      to="/characters"
                      className={`nav-link ${isActive('/characters') ? 'nav-link-active' : 'nav-link-inactive'}`}
                      aria-label="Characters"
                      title="My characters"
                    >
                      <User className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">Characters</span>
                    </Link>
                    <Link
                      to="/campaigns"
                      className={`nav-link ${isActive('/campaigns') ? 'nav-link-active' : 'nav-link-inactive'}`}
                      aria-label="Campaigns"
                      title="My campaigns and parties"
                    >
                      <Tent className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">Campaigns</span>
                    </Link>
                    <Link
                      to="/wiki"
                      className={`nav-link ${isActive('/wiki') ? 'nav-link-active' : 'nav-link-inactive'}`}
                      aria-label="Wikis"
                      title="Every campaign wiki you can read"
                    >
                      <BookOpen className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">Wikis</span>
                      {wikiMarker}
                    </Link>
                    <Link
                      to="/favorites"
                      className={`nav-link ${isActive('/favorites') ? 'nav-link-active' : 'nav-link-inactive'}`}
                      aria-label="Favorites"
                      title="Favorites"
                    >
                      <Heart className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">Favorites</span>
                    </Link>
                    <Link
                      to="/account"
                      className={`nav-link ${isActive('/account') ? 'nav-link-active' : 'nav-link-inactive'}`}
                      aria-label="Account"
                      title={`Account (${user?.display_name || user?.email || ''})`}
                    >
                      <UserCog className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">Account</span>
                      {inviteBadge}
                    </Link>
                    <button
                      type="button"
                      onClick={logout}
                      className="nav-link nav-link-inactive"
                      aria-label="Log out"
                      title="Log out"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      <span className="header-icon-label">Log out</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="nav-link nav-link-inactive">Log in</Link>
                    <Link to="/register" className="nav-link nav-link-inactive">Sign up</Link>
                  </>
                )}
              </div>
              <SystemSelector />
              {/* Typography preferences (#199): text size, density, font */}
              <TypographyMenu />
              <button
                onClick={toggleDarkMode}
                className="dark-mode-toggle"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Sun className="w-4.5 h-4.5" style={{ width: '1.1rem', height: '1.1rem' }} /> : <Moon style={{ width: '1.1rem', height: '1.1rem' }} />}
              </button>

              <div className={bp('md:hidden')}>
                <button
                  onClick={() => { setIsSearchOpen(!isSearchOpen); setIsMenuOpen(false); }}
                  className="mobile-menu-button"
                  aria-label={isSearchOpen ? 'Close search' : 'Open search'}
                  aria-expanded={isSearchOpen}
                  aria-controls="mobile-search"
                >
                  {isSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
                </button>
              </div>

              <div className={bp('md:hidden')}>
                <button
                  onClick={() => { setIsMenuOpen(!isMenuOpen); setIsSearchOpen(false); }}
                  className="mobile-menu-button"
                  aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={isMenuOpen}
                  aria-controls="mobile-menu"
                >
                  {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile search row (#139) ───────────────────────────── */}
        {isSearchOpen && (
          <div
            id="mobile-search"
            className={bp('md:hidden px-3 pb-3 pt-1')}
            style={{ backgroundColor: 'var(--chrome-bg)', borderTop: '1px solid var(--chrome-border)' }}
          >
            <OmniSearch autoFocus onNavigate={() => setIsSearchOpen(false)} />
          </div>
        )}

        {/* ── Mobile Nav ─────────────────────────────────────────── */}
        {isMenuOpen && (
          <nav
            id="mobile-menu"
            aria-label="Mobile navigation"
            className="max-h-[calc(100dvh-3.5rem)] overflow-y-auto overscroll-contain"
            style={{ backgroundColor: 'var(--chrome-bg)', borderTop: '1px solid var(--chrome-border)' }}
          >
            <div className="px-3 pt-2 pb-3 space-y-1.5">
              {navGroups.map((group) => {
                const groupOpen   = !!openMobileGroups[group.key];
                const groupActive = isGroupActive(group);
                return (
                  <div
                    key={group.key}
                    className="rounded overflow-hidden"
                    style={{ border: '1px solid rgba(201,168,76,0.2)' }}
                  >
                    <button
                      className="w-full flex items-center justify-between px-3 py-2.5 transition-colors duration-150"
                      style={{
                        fontFamily: 'Cinzel, Georgia, serif',
                        fontSize: '0.78rem',
                        letterSpacing: '0.04em',
                        fontWeight: 600,
                        color: groupActive ? 'var(--chrome-text-active)' : 'var(--chrome-text)',
                        backgroundColor: groupActive ? 'rgba(201,168,76,0.08)' : 'transparent',
                      }}
                      onClick={() => setOpenMobileGroups((prev) => ({ ...prev, [group.key]: !groupOpen }))}
                      aria-expanded={groupOpen}
                    >
                      <span>{group.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${groupOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {groupOpen && (
                      <div style={{ backgroundColor: 'var(--chrome-bg-deep)', borderTop: '1px solid rgba(201,168,76,0.12)' }}>
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isUnderConstruction = isPageUnderConstruction(item.href);
                          const active = isActive(item.href);
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              onClick={() => setIsMenuOpen(false)}
                              className="flex items-center gap-2 px-4 py-2.5 transition-colors duration-150"
                              style={{
                                fontFamily: 'Crimson Text, Georgia, serif',
                                fontSize: '1rem',
                                color: active ? 'var(--chrome-text-active)' : 'var(--chrome-text)',
                                backgroundColor: active ? 'rgba(201,168,76,0.07)' : 'transparent',
                              }}
                            >
                              <Icon className="w-4 h-4 flex-shrink-0" />
                              <span>{item.name}</span>
                              {isUnderConstruction && (
                                <Construction className="w-4 h-4 ml-auto" style={{ color: '#C9A84C' }} />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Account section — the auth cluster lives here below md (P0.3) */}
              <div
                className="rounded overflow-hidden"
                style={{ border: '1px solid rgba(201,168,76,0.2)' }}
              >
                <div
                  className="px-3 py-2.5"
                  style={{
                    fontFamily: 'Cinzel, Georgia, serif',
                    fontSize: '0.78rem',
                    letterSpacing: '0.04em',
                    fontWeight: 600,
                    color: 'var(--chrome-text)',
                  }}
                >
                  Account
                </div>
                <div style={{ backgroundColor: 'var(--chrome-bg-deep)', borderTop: '1px solid rgba(201,168,76,0.12)' }}>
                  {isAuthenticated ? (
                    <>
                      {[
                        { name: 'My content', href: '/authoring', icon: Hammer },
                        { name: 'Characters', href: '/characters', icon: User },
                        { name: 'Campaigns', href: '/campaigns', icon: Tent },
                        { name: 'Campaign wikis', href: '/wiki', icon: BookOpen, badge: wikiMarker },
                        { name: 'Favorites', href: '/favorites', icon: Heart },
                        { name: 'Account',   href: '/account',   icon: UserCog, badge: inviteBadge },
                        ...(user?.is_admin ? [
                          { name: 'Reports', href: '/admin/reports', icon: Flag },
                          { name: 'Moderation', href: '/admin/content', icon: ShieldCheck },
                          { name: 'Verification', href: '/admin/verification', icon: ClipboardCheck },
                        ] : []),
                      ].map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.href);
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center gap-2 px-4 py-2.5 transition-colors duration-150"
                            style={{
                              fontFamily: 'Crimson Text, Georgia, serif',
                              fontSize: '1rem',
                              color: active ? 'var(--chrome-text-active)' : 'var(--chrome-text)',
                              backgroundColor: active ? 'rgba(201,168,76,0.07)' : 'transparent',
                            }}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span>{item.name}</span>
                            {item.badge}
                          </Link>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => { setIsMenuOpen(false); logout(); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 transition-colors duration-150"
                        style={{
                          fontFamily: 'Crimson Text, Georgia, serif',
                          fontSize: '1rem',
                          color: 'var(--chrome-text)',
                        }}
                      >
                        <LogOut className="w-4 h-4 flex-shrink-0" />
                        <span>Log out</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 transition-colors duration-150"
                        style={{
                          fontFamily: 'Crimson Text, Georgia, serif',
                          fontSize: '1rem',
                          color: isActive('/login') ? 'var(--chrome-text-active)' : 'var(--chrome-text)',
                        }}
                      >
                        <LogIn className="w-4 h-4 flex-shrink-0" />
                        <span>Log in</span>
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 transition-colors duration-150"
                        style={{
                          fontFamily: 'Crimson Text, Georgia, serif',
                          fontSize: '1rem',
                          color: isActive('/register') ? 'var(--chrome-text-active)' : 'var(--chrome-text)',
                        }}
                      >
                        <UserPlus className="w-4 h-4 flex-shrink-0" />
                        <span>Sign up</span>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </nav>
        )}
      </header>

      {/* One honest sentence for the whole app when there is no connection
          (issue 312), directly under the chrome so it is seen without being
          in the way. It renders nothing while online. */}
      <OfflineNotice />

      {/* ── Main Content ─────────────────────────────────────────── */}
      <main className="main-content w-full">
        <SessionEndedNotice />
        <HandleBanner />
        {children}
      </main>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="footer mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="footer-text">
            <p>&copy; {new Date().getFullYear()} The Dragon's Table. All rights reserved.</p>
            <p className="mt-2 text-sm">
              The Dragon's Table is an independent reference compatible with the D&amp;D 3.5e and
              Pathfinder 1e rulesets. It is not affiliated with, endorsed, sponsored by, or
              approved of by Wizards of the Coast LLC or Paizo Inc. Dungeons &amp; Dragons is a
              trademark of Wizards of the Coast LLC; Pathfinder is a trademark of Paizo Inc.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              {/* The features guide (#318) leads, because the footer is the
                  one piece of chrome on every page and a guide nobody can find
                  is the failure this issue exists to avoid. */}
              <Link to="/guide" className="footer-link">What you can do here</Link>
              <span className="footer-divider hidden sm:inline" aria-hidden="true">|</span>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
              <span className="footer-divider hidden sm:inline" aria-hidden="true">|</span>
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <span className="footer-divider hidden sm:inline" aria-hidden="true">|</span>
              <Link to="/ogl" className="footer-link">Open Game License</Link>
              <span className="footer-divider hidden sm:inline" aria-hidden="true">|</span>
              <button
                type="button"
                onClick={() => { setReportInitial(null); setIsReportOpen(true); }}
                onPointerEnter={warmReportModal}
                onFocus={warmReportModal}
                className="footer-link"
              >
                Report a problem
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Problem-report modal (#59) — reachable from every page's footer */}
      {(reportUsed || isReportOpen) && (
        <Suspense fallback={null}>
          <ReportModal isOpen={isReportOpen} initial={reportInitial} onClose={() => setIsReportOpen(false)} />
        </Suspense>
      )}

      {/* Command palette (#198) — Cmd/Ctrl-K anywhere; mounted once here so it
          is available on every page. Theme state stays Layout's, so the toggle
          command goes through the same handler as the header button. */}
      <CommandPalette isDarkMode={isDarkMode} onToggleTheme={toggleDarkMode} />

      {/* Hover cards (#73) — one delegated card for every `a[data-preview]`
          on the page: reference links, list cards, the similar rail. */}
      <HoverCardLayer />
    </div>
  );
};

export default Layout;
